import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../ui/button";

type Props = {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Total row count from the server, when the API reports it. */
  totalItems?: number;
  isFetching?: boolean;
};

/**
 * Server-side pagination controls. The APIs here page on the server, so this
 * drives request params rather than TanStack's client-side row model.
 */
const DataTablePagination = ({
  page,
  totalPages,
  onPageChange,
  totalItems,
  isFetching,
}: Props) => {
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="flex flex-col items-center justify-between gap-3 border-t px-4 py-3 mds:flex-row">
      <p className="text-[13px] font-medium text-muted-foreground">
        {typeof totalItems === "number"
          ? `${totalItems.toLocaleString()} ${totalItems === 1 ? "result" : "results"}`
          : " "}
        {isFetching && <span className="ml-2 opacity-70">Updating…</span>}
      </p>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={!canPrev}
          onClick={() => onPageChange(page - 1)}
          className="h-9 gap-1 bg-background"
        >
          <ChevronLeft className="size-4" />
          Previous
        </Button>

        <span className="px-2 text-[13px] font-semibold text-foreground">
          Page {page} of {Math.max(totalPages, 1)}
        </span>

        <Button
          variant="outline"
          size="sm"
          disabled={!canNext}
          onClick={() => onPageChange(page + 1)}
          className="h-9 gap-1 bg-background"
        >
          Next
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
};

export default DataTablePagination;
